import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CitizenCardUpload } from '../components/CitizenCardUpload';
import { Loader2, CheckCircle2, PenLine, Eraser, ShieldCheck, Zap, Flame } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Signature pad — draws with finger/mouse, transparent background
const SignaturePad = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const setup = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
  }, []);

  useEffect(() => { setup(); window.addEventListener('resize', setup); return () => window.removeEventListener('resize', setup); }, [setup]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    hasDrawn.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasDrawn.current) onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
    onChange('');
  };

  return (
    <div>
      <div className="relative rounded-xl border-2 border-dashed border-slate-300 bg-slate-50">
        <canvas
          ref={canvasRef}
          className="w-full h-44 touch-none rounded-xl"
          style={{ touchAction: 'none' }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="signature-canvas"
        />
        <span className="absolute top-2 left-3 text-[11px] text-slate-400 flex items-center gap-1 pointer-events-none"><PenLine className="w-3 h-3" /> Assine aqui com o dedo</span>
      </div>
      <button type="button" onClick={clear} className="mt-2 text-xs text-slate-500 flex items-center gap-1 hover:text-slate-700" data-testid="clear-signature-btn">
        <Eraser className="w-3.5 h-3.5" /> Limpar assinatura
      </button>
    </div>
  );
};

export const PublicSignPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [pageImgs, setPageImgs] = useState([]);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [locality, setLocality] = useState('');
  const [consent, setConsent] = useState(false);
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [ccOk, setCcOk] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/public/sign/${token}`);
        if (!res.ok) { setError('Este link é inválido ou já expirou.'); setLoading(false); return; }
        const data = await res.json();
        setInfo(data);
        setName(data.client_name || '');
        setContact(data.client_contact || '');
        setLocality(data.client_locality || '');
        if (data.status === 'assinado') setDone(true);
        const imgs = [];
        for (let i = 0; i < (data.page_count || 1); i++) {
          const r = await fetch(`${API_URL}/public/sign/${token}/page-image?page=${i}`);
          if (r.ok) imgs.push(URL.createObjectURL(await r.blob()));
        }
        setPageImgs(imgs);
      } catch {
        setError('Não foi possível carregar o documento.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!consent) { return; }
    if (!signature) { return; }
    if (!name.trim()) { return; }
    if (info?.require_cc && !ccOk) { return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/public/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consent, name: name.trim(), contact: contact.trim(), locality: locality.trim(), signature_image: signature }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Erro ao submeter');
      }
      setDone(true);
    } catch (e) {
      setError(e.message || 'Erro ao submeter a assinatura.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div>;
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
        <div className="bg-white rounded-2xl shadow p-8 max-w-md text-center">
          <p className="text-slate-700">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center" data-testid="sign-success">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Documento assinado!</h1>
          <p className="text-sm text-slate-500">Obrigado, {name || 'cliente'}. A sua autorização foi registada com sucesso. Pode fechar esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-28">
      <header className="bg-gradient-to-r from-[#1f3a8a] to-[#0d9488] text-white px-5 py-4 flex items-center gap-3 sticky top-0 z-10">
        <div className="flex items-center gap-1 bg-white/15 rounded-xl px-2.5 py-2 backdrop-blur-sm">
          <Zap className="h-5 w-5 text-amber-300" />
          <Flame className="h-5 w-5 text-orange-300" />
        </div>
        <div>
          <p className="text-sm font-semibold leading-tight">{info?.document_name || 'Autorização'}</p>
          <p className="text-[11px] text-white/80">Autorização de Luz e Gás · Assinatura digital</p>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        <div className="bg-white rounded-2xl shadow-sm p-3 space-y-3">
          <p className="text-xs text-slate-500 px-1">Leia o documento abaixo antes de assinar.</p>
          {pageImgs.map((u, i) => (
            <img key={i} src={u} alt={`Página ${i + 1}`} className="w-full rounded-lg border border-slate-200" data-testid={`sign-page-${i}`} />
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600">Nome completo</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" data-testid="sign-name-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Contacto</label>
            <input value={contact} onChange={(e) => setContact(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" data-testid="sign-contact-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Localidade</label>
            <input value={locality} onChange={(e) => setLocality(e.target.value)} placeholder="Ex: Lisboa" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" data-testid="sign-locality-input" />
          </div>

          <label className="flex items-start gap-2.5 bg-slate-50 border border-slate-200 rounded-xl p-3 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#2D3E6F]" data-testid="sign-consent-checkbox" />
            <span className="text-xs text-slate-600 leading-relaxed flex items-start gap-1">
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              Autorizo o tratamento dos meus dados pessoais e a assinatura digital deste documento, de acordo com a política de proteção de dados (RGPD).
            </span>
          </label>

          {info?.require_cc && (
            <CitizenCardUpload token={token} empresa={info.empresa} initial={info.cc} onChange={setCcOk} />
          )}

          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">A sua assinatura</label>
            <SignaturePad onChange={setSignature} />
          </div>

          {error && <p className="text-xs text-rose-600">{error}</p>}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={submit}
            disabled={submitting || !consent || !signature || !name.trim() || (info?.require_cc && !ccOk)}
            className="w-full bg-[#2D3E6F] disabled:opacity-40 text-white font-semibold rounded-xl py-3.5 flex items-center justify-center gap-2"
            data-testid="sign-submit-btn"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <PenLine className="w-5 h-5" />}
            Confirmar e Assinar
          </button>
          {(!consent || !signature || (info?.require_cc && !ccOk)) && (
            <p className="text-[11px] text-slate-400 text-center mt-1.5" data-testid="sign-hint">
              {!consent ? 'Aceite o consentimento RGPD' : (info?.require_cc && !ccOk) ? 'Carregue o Cartão de Cidadão (frente e verso)' : 'Desenhe a sua assinatura'} para continuar
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicSignPage;
